import { Box, Tab, Tabs, Tooltip, type TabProps } from '@mui/material';
import type { ReactNode, SyntheticEvent } from 'react';
import { designTokens } from '../tokens';
import { capsuleTabItemStyles, capsuleTabTrackStyles, tabItemStyles, tabListStyles } from './tabStyles';
import { usePageTabs } from './PageTabsContext';
import { segmentedControlItemStyles, segmentedControlRailStyles } from './segmentedControlStyles';

export type PillTabItem<Value extends string> = {
  value: Value;
  tabId: string;
  panelId: string;
  ariaLabel?: string;
  disabled?: boolean;
} & ({
  label: ReactNode;
  icon?: ReactNode;
  iconOnly?: false;
} | {
  /** 아이콘 전용 탭의 접근 가능한 이름과 도움말입니다. */
  label: string;
  icon: ReactNode;
  iconOnly: true;
});

// Tabs가 주입한 선택·키보드 prop을 실제 Tab까지 전달하며 Tooltip은 DOM을 감싸지 않습니다.
function IconPillTab({ tooltip, ...props }: TabProps & { tooltip: string }) {
  const tab = <Tab {...props} />;
  return props.disabled ? tab : <Tooltip title={tooltip}>{tab}</Tooltip>;
}

export type PillTabsProps<Value extends string> = {
  value: Value;
  items: readonly PillTabItem<Value>[];
  ariaLabel: string;
  onChange: (value: Value) => void;
  edgeAlignment?: 'control' | 'label';
  /** capsule은 페이지 탭과 같은 흰 묶음 표면, segmented는 간격 없이 연결된 선택 rail입니다. */
  appearance?: 'pills' | 'capsule' | 'segmented';
  selectionFollowsFocus?: boolean;
};

export function PillTabs<Value extends string>({
  value,
  items,
  ariaLabel,
  onChange,
  edgeAlignment = 'control',
  appearance = 'pills',
  selectionFollowsFocus = true,
}: PillTabsProps<Value>) {
  const pageTabs = usePageTabs();
  const handleChange = (_event: SyntheticEvent, nextValue: Value) => onChange(nextValue);
  const segmented = appearance === 'segmented';
  const capsule = !segmented && (pageTabs || appearance === 'capsule');

  return <Tabs
    value={value}
    onChange={handleChange}
    aria-label={ariaLabel}
    selectionFollowsFocus={selectionFollowsFocus}
    variant="scrollable"
    scrollButtons={false}
    sx={{
      ml: !pageTabs && !capsule && edgeAlignment === 'label' ? `-${designTokens.space.md}px` : 0,
      ...tabListStyles,
      ...(capsule ? capsuleTabTrackStyles : {}),
      ...(segmented ? { ...segmentedControlRailStyles, display: 'inline-flex', flexShrink: 0, width: 'fit-content', maxWidth: '100%', gap: 0 } : {}),
      '& .MuiTabs-indicator': { display: 'none' },
      '& .MuiTabs-list, & .MuiTabs-flexContainer': { ...tabListStyles, ...(segmented || capsule ? { gap: designTokens.space.none } : {}) },
      '& .MuiTab-root': segmented ? {
        ...segmentedControlItemStyles,
        maxWidth: 'none', border: 0, backgroundColor: 'transparent', textTransform: 'none', letterSpacing: 'normal',
      } : capsule ? capsuleTabItemStyles : tabItemStyles,
      ...(!segmented ? { '& .MuiTab-root[data-icon-only="true"]': {
        minWidth: designTokens.layout.pillTabHeight,
        px: 0,
        [designTokens.media.touchTarget]: { minWidth: designTokens.layout.touchTargetMinSize },
      } } : {}),
    }}
  >
    {items.map((item) => {
      const props: TabProps = {
        id: item.tabId,
        value: item.value,
        label: item.icon ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: `${designTokens.space.xs}px` }}>
          <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', '& svg': { fontSize: designTokens.layout.pageHeaderControlGlyphSize } }}>{item.icon}</Box>
          {!item.iconOnly && item.label}
        </Box> : item.label,
        'aria-label': item.ariaLabel ?? (item.iconOnly ? item.label : undefined),
        'aria-controls': item.panelId,
        disabled: item.disabled,
        disableRipple: true,
      };
      return item.iconOnly
        ? <IconPillTab key={item.value} {...props} data-icon-only="true" tooltip={item.ariaLabel ?? item.label} />
        : <Tab key={item.value} {...props} />;
    })}
  </Tabs>;
}
